import { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { NextHead } from "@components/nextHead";
import { useInView } from "react-intersection-observer";

import { makeLpTitle } from "@utils/makeLpTitle";
import {
  BestInTravelProvider,
  useBestInTravel,
} from "@context/bestInTravel/2025/bestInTravelContext";

import data from "@constants/best-in-travel/2025";
import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";

import { Loading } from "@components/loading";
import { Hero } from "@pageComponents/best-in-travel-2025/hero";
import { MapWrapper } from "@pageComponents/best-in-travel-2025/map";

import { DestinationsNav } from "@pageComponents/best-in-travel-2025/destinationsNav";
import { DestinationsWrapper } from "@pageComponents/best-in-travel-2025/destinationsWrapper";
import { VideoModal } from "@pageComponents/best-in-travel-2025/videoModal";
import { ShareDrawer } from "@pageComponents/best-in-travel-2025/shareDrawer";
import { tagular } from "@cohesion/tagular";

function BestInTravelContent({
  campaign,
  category = null,
  destinations,
  inactiveDestinations = null,
  meta,
}) {
  const { mapSectionRef } = useBestInTravel();
  const { ref: mapRef, inView: mapInView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });
  const { ref: booksRef, inView: booksInView } = useInView({
    threshold: 1,
    triggerOnce: true,
  });

  const setRefs = useCallback(
    node => {
      mapSectionRef.current = node;
      mapRef(node);
    },
    [mapRef, mapSectionRef]
  );

  useEffect(() => {
    if (mapInView) {
      tagular("view", {
        webElement: {
          location: "BIT_MAP",
          elementType: "SECTION",
        },
      });
    }
  }, [mapInView, mapRef]);

  useEffect(() => {
    if (booksInView) {
      tagular("view", {
        webElement: {
          location: "BIT_Book_Section",
          elementType: "SECTION",
        },
      });
    }
  }, [booksInView, booksRef]);

  useEffect(() => {
    const container = document.getElementById("wrapperContainer");
    const navbar = document.querySelector(".bit-navbar"); // Select the navbar by class

    if (!container || !navbar) {
      // Return a no-op function to keep return types consistent
      return () => {};
    }

    // Initial check for scroll position
    if (container.scrollTop > 50) {
      navbar.classList.add("smooth-hidden"); // Hide the navbar smoothly
    }

    const handleScroll = () => {
      if (container.scrollTop > 50) {
        navbar.classList.add("smooth-hidden"); // Hide the navbar smoothly
      } else {
        navbar.classList.remove("smooth-hidden"); // Show the navbar smoothly
      }
    };

    container.addEventListener("scroll", handleScroll);

    // Cleanup function
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return (
    <>
      <NextHead image={{ url: meta?.image }}>
        <title>{makeLpTitle(meta?.title)}</title>
        <link key="canonical" rel="canonical" href={meta?.canonicalUrl} />
        <meta key="title" name="title" content={makeLpTitle(meta?.title)} />
        <meta
          key="description"
          name="description"
          content={meta?.description}
        />
        <meta key="og:url" property="og:url" content={meta?.canonicalUrl} />
        <meta
          key="og:title"
          property="og:title"
          content={makeLpTitle(meta?.title)}
        />
        <meta
          key="og:description"
          property="og:description"
          content={meta?.description}
        />
        <meta
          key="og:image:alt"
          property="og:image:alt"
          content={makeLpTitle(meta?.title)}
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={makeLpTitle(meta?.title)}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={meta?.description}
        />
        <meta
          key="twitter:image:alt"
          name="twitter:image:alt"
          content={makeLpTitle(meta?.title)}
        />
      </NextHead>

      <div
        id="wrapperContainer"
        className="overflow-x-hidden overflow-y-scroll h-screen-dvh no-scrollbar snap-y snap-mandatory"
      >
        {/* HERO SECTION */}
        <div className="flex items-center justify-center snap-start h-screen-dvh">
          <Hero category={category} campaign={campaign} />
        </div>

        {/* MAP SECTION */}
        <div ref={setRefs} className="snap-start h-screen-dvh" id="map">
          {mapInView ? (
            <MapWrapper
              destinations={destinations}
              inactiveDestinations={inactiveDestinations}
              routeCategory={category?.name}
            />
          ) : (
            <section className="relative flex flex-col items-center justify-center w-full bg-sand-200 h-screen-dvh">
              <Loading />
            </section>
          )}
        </div>

        {/* DESTINATIONS SECTIONS */}
        <div className="relative flex items-center justify-center snap-start h-screen-dvh snap-y snap-mandatory snap-always">
          {/* DESTINATIONS SECTIONS WRAPPER */}
          <DestinationsWrapper destinations={destinations} />
        </div>
      </div>

      {/* DESTINATIONS NAVIGATION */}
      <DestinationsNav category={category} />

      {/* SHARE DRAWER */}
      <ShareDrawer />

      {/* VIDEO MODAL */}
      <VideoModal />

      <style jsx global>{`
        .bit-navbar {
          transition:
            opacity 0.25s ease,
            visibility 0.25s ease,
            max-height 0.25s ease;
          opacity: 1;
          visibility: visible;
          max-height: 64px;
        }

        .smooth-hidden {
          opacity: 0;
          visibility: hidden;
          max-height: 0;
          overflow: hidden;
        }
      `}</style>
    </>
  );
}

BestInTravelContent.propTypes = {
  campaign: PropTypes.shape({
    title: PropTypes.string.isRequired,
    media: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(["video", "image"]).isRequired,
        orientation: PropTypes.oneOf(["portrait", "landscape"]).isRequired,
        src: PropTypes.shape({
          poster: PropTypes.string.isRequired,
          mp4: PropTypes.string.isRequired,
          webm: PropTypes.string.isRequired,
        }).isRequired,
      })
    ).isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  }).isRequired,
  category: BIT_PROP_TYPES.category,
  destinations: PropTypes.arrayOf(BIT_PROP_TYPES.destination).isRequired,
  inactiveDestinations: PropTypes.arrayOf(BIT_PROP_TYPES.destination),
};

export default function BestInTravel(props) {
  return (
    <BestInTravelProvider>
      <BestInTravelContent {...props} />
    </BestInTravelProvider>
  );
}

export async function getServerSideProps() {
  const { destinations, categories, meta } = data;

  return {
    props: {
      campaign: {
        title: data.title,
        description: data.description,
        media: data.media,
      },
      categories,
      destinations,
      disableAlert: true,
      meta,
      navbarClassName: "bit-navbar",
    },
  };
}
